import type { ReactNode } from 'react';
import { useFormat } from '@paige/format';
import { CompanyName } from '../TestMark';
import { dayWord } from '../insights/account-words';
import { healthOf } from '../insights/health';
import type { CustomerRow } from './customer-types';
import { changeOf, packageWord } from './customer-words';

/** The router's link to a company's page. */
export type CompanyLink = (id: string, children: ReactNode) => ReactNode;

/** Under this many days of credits the row says when they run out (the ledger's Running out rule). */
export const RUNWAY_SOON = 14;

/** The name as a real link with the Test mark; the package and the partner quiet under it. */
export function NameCell({ row, renderLink }: { row: CustomerRow; renderLink: CompanyLink }) {
  return (
    <span className="ad-cu-name">
      {renderLink(row.id, <CompanyName name={row.name} test={row.testCompany} />)}
      <span className="ad-cu-name__sub">
        {packageWord(row.plan)}
        {row.partner ? ` · partner ${row.partner}` : ''}
      </span>
    </span>
  );
}

/** Pages read in the period, with the change on the period before as an arrow that says its words. */
export function PagesCell({ row }: { row: CustomerRow }): ReactNode {
  const f = useFormat();
  const change = changeOf(row.pages, row.previousPages, (n) => f.int(n));
  return (
    <span className="ad-cu-pages">
      {f.int(row.pages)}
      <span className={`ad-cu-change ad-cu-change--${change.tone}`} title={change.text}>
        <span aria-hidden="true">{change.arrow}</span>
        <span className="pui-visually-hidden">{change.text}</span>
      </span>
    </span>
  );
}

/** Card money in the period; nothing is quiet. */
export function MoneyCell({ row }: { row: CustomerRow }): ReactNode {
  const f = useFormat();
  return <span className={row.paid === 0 ? 'ad-cu-quiet' : undefined}>{f.money(row.paid)}</span>;
}

/** Where the account is going: the same word the company's own page says, with its glyph. */
export function HealthCell({ row, today }: { row: CustomerRow; today: string }): ReactNode {
  const health = healthOf({
    today,
    firstPagesAt: row.firstPagesAt,
    lastPagesAt: row.lastPagesAt,
    last30: row.last30,
    prior30: row.prior30,
  });
  return (
    <span
      className={`ad-health-word ad-health-word--${health.word.replace(' ', '-')}`}
      title={health.why}
    >
      <span aria-hidden="true">{health.icon}</span> {health.word}
    </span>
  );
}

/** What the row needs attention for, most pressing first; an empty list when it is fine. */
export function attentionOf(row: CustomerRow): { text: string; tone: 'danger' | 'warning' }[] {
  const out: { text: string; tone: 'danger' | 'warning' }[] = [];
  if (row.pastDue) out.push({ text: 'Past due', tone: 'danger' });
  if (row.problems > 0) {
    out.push({
      text: `${row.problems} ${row.problems === 1 ? 'problem' : 'problems'}`,
      tone: 'danger',
    });
  }
  if (row.creditsHeld) out.push({ text: 'Held for pages', tone: 'warning' });
  else if (row.runwayDays !== null && row.runwayDays < RUNWAY_SOON) {
    const days = Math.max(0, Math.floor(row.runwayDays));
    out.push({
      text: days === 0 ? 'Runs out today' : `Runs out in ${days} ${days === 1 ? 'day' : 'days'}`,
      tone: 'warning',
    });
  }
  return out;
}

export function AttentionCell({ row }: { row: CustomerRow }): ReactNode {
  const items = attentionOf(row);
  if (items.length === 0) return null;
  return (
    <span className="ad-cu-flags">
      {items.map((item) => (
        <span key={item.text} className={`ad-cu-flag ad-cu-flag--${item.tone}`}>
          {item.text}
        </span>
      ))}
    </span>
  );
}

/** The open next step and its day; a warning sign when the day has passed. */
export function NextStepCell({ row, today }: { row: CustomerRow; today: string }): ReactNode {
  const step = row.nextStep;
  if (!step) return null;
  const overdue = step.dueOn !== null && step.dueOn < today;
  return (
    <span className="ad-cu-step" title={step.body}>
      {overdue ? (
        <span className="ad-cu-step__late" title="Past its due day">
          <span aria-hidden="true">⚠</span>
          <span className="pui-visually-hidden">Overdue: </span>
        </span>
      ) : null}
      <span className="ad-cu-step__body">{step.body}</span>
      {step.dueOn ? <span className="ad-cu-quiet"> · {dayWord(step.dueOn)}</span> : null}
    </span>
  );
}

export function LastActiveCell({ row }: { row: CustomerRow }): ReactNode {
  const f = useFormat();
  return row.lastActiveAt ? (
    <span title={f.dateTime(row.lastActiveAt)}>{f.ago(row.lastActiveAt)}</span>
  ) : (
    <span className="ad-cu-quiet">never</span>
  );
}
